import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import {
  Archive,
  Calendar,
  CheckCircle,
  Download,
  FileDown,
  Loader2,
  Printer,
  Star,
  XCircle,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { downloadMarkdown, downloadReportPdf } from "@/lib/reportExport";
import { getClientIdentityHeaders } from "@/lib/clientIdentity";
import InformationDisplay, { formatInformationTime } from "@/components/InformationDisplay";
import type { InformationState } from "@shared/informationState";
import type { WhoResearchEvidenceSnapshot } from "@shared/intelligenceReadiness";
import type { ResearchManifestSummary } from "@shared/researchManifest";
import ResearchManifestEvidence from "@/components/ResearchManifestEvidence";
import ResearchDossierReport from "@/components/ResearchDossierReport";
import ExposureReactionDossierReport from "@/components/ExposureReactionDossierReport";
import ProspectiveCohortPanel from "@/components/ProspectiveCohortPanel";
import EventEvidenceReviewReport from "@/components/EventEvidenceReviewReport";

type ResearchType = "digital-asset-trader" | "tradfi" | "digital-assets" | "intelligence-evidence" | "ideas";

interface ReportArchiveItem {
  id: string;
  symbol: string;
  title: string;
  researchType: ResearchType;
  assetClass: string;
  provider: string;
  model: string | null;
  markdown?: string;
  summary: string;
  sourceCount: number;
  createdAt: string;
  isFeatured: boolean;
  featuredSlug: string | null;
  featuredExcerpt: string | null;
  snapshot?: SavedProviderSnapshot | WhoResearchEvidenceSnapshot | null;
  manifestSummary?: ResearchManifestSummary;
  canExportManifest?: boolean;
}

interface SavedProviderSnapshot {
  generatedAt?: string;
  sources?: { provider: string; label: string; information?: InformationState }[];
  observations?: { provider: string; label: string; values: Record<string, string | number>; instrument?: string | null; venue?: string | null; unit?: string | null; currency?: string | null; information?: InformationState }[];
}

export function SavedSnapshotInformation({ snapshot }: { snapshot?: SavedProviderSnapshot | null }) {
  return <details style={{ margin: "12px 0" }}>
    <summary>Saved provider evidence</summary>
    <p>Evidence state at report creation. The saved report text is historical.</p>
    {snapshot?.generatedAt && <p>Snapshot assembled: {formatInformationTime(snapshot.generatedAt)}</p>}
    {!snapshot?.sources?.length && !snapshot?.observations?.length && <InformationDisplay captured />}
    {snapshot?.sources?.map((source, index) => <div key={`${source.provider}-${index}`}>
      <strong>{source.label} · {source.provider}</strong>
      <InformationDisplay information={source.information} captured />
    </div>)}
    {snapshot?.observations?.map((observation, index) => <div key={`${observation.provider}-${index}`}>
      <strong>{observation.label} · {observation.provider}{observation.instrument ? ` · ${observation.instrument}` : ""}{observation.venue ? ` · ${observation.venue}` : ""}</strong>
      <p>{Object.entries(observation.values).map(([key, value]) => `${key}: ${value}`).join(" · ")}{observation.unit ? ` ${observation.unit}` : ""}{observation.currency ? ` · ${observation.currency}` : ""}</p>
      <InformationDisplay information={observation.information} captured />
    </div>)}
  </details>;
}

function isWhoResearchSnapshot(snapshot: ReportArchiveItem["snapshot"]): snapshot is WhoResearchEvidenceSnapshot {
  return !!snapshot && typeof snapshot === "object" && "version" in snapshot
    && snapshot.version === "tsm-who-research-snapshot-v1" && "reports" in snapshot && Array.isArray(snapshot.reports);
}

export function SavedWhoResearchSnapshot({ snapshot }: { snapshot: WhoResearchEvidenceSnapshot }) {
  return <details style={{ margin: "12px 0" }}>
    <summary>Captured WHO research evidence</summary>
    <p>Provider-reported WHO evidence as saved. It does not confirm a new outbreak or market impact. This view uses the saved copy and does not refresh current source data.</p>
    <p>Captured: {formatInformationTime(snapshot.capturedAt)} · Evidence digest: {snapshot.evidenceHash} · Method: {snapshot.method}</p>
    {snapshot.reports.map((item) => <article key={item.observation.revisionKey}>
      <strong>{item.observation.title}</strong>
      <p>WHO report {item.observation.providerReportId} · Published: {item.observation.publishedAt ? formatInformationTime(item.observation.publishedAt) : "Unknown"} · Provider updated: {item.observation.sourceUpdatedAt ? formatInformationTime(item.observation.sourceUpdatedAt) : "Unknown"}</p>
      <p>{item.observation.summary}</p>
      <a href={item.observation.sourceUrl} target="_blank" rel="noreferrer">Open original WHO report</a>
      <InformationDisplay information={item.information} captured />
    </article>)}
  </details>;
}

export function reportTypeLabel(type: ResearchType) {
  if (type === "digital-asset-trader") return "Trader Research";
  if (type === "digital-assets") return "Digital Assets";
  if (type === "intelligence-evidence") return "WHO Evidence";
  if (type === "ideas") return "Saved Ideas";
  return "TradFi";
}

function isExposureReactionReport(report: ReportArchiveItem) {
  return report.manifestSummary?.version === "tsm-research-manifest-v3";
}

export function isEventEvidenceReviewReport(report: ReportArchiveItem) {
  return ["tsm-research-manifest-v4", "tsm-research-manifest-v5", "tsm-research-manifest-v6", "tsm-research-manifest-v7", "tsm-research-manifest-v8", "tsm-research-manifest-v9", "tsm-research-manifest-v10"].includes(String(report.manifestSummary?.version));
}

export function isIdeasRetentionReport(report: ReportArchiveItem) {
  return report.researchType === "ideas" || report.manifestSummary?.version === "tsm-research-manifest-v11";
}

export function isPublicBestOfReport(report: ReportArchiveItem) {
  return report.researchType !== "ideas"
    && report.researchType !== "intelligence-evidence"
    && !isExposureReactionReport(report);
}

export function reportDisplayLabel(report: ReportArchiveItem) {
  if (isIdeasRetentionReport(report)) return "Saved Ideas";
  if (isEventEvidenceReviewReport(report)) return "Event Evidence Review";
  return isExposureReactionReport(report) ? "Exposure and Response" : reportTypeLabel(report.researchType);
}

export function reportCardSourceCount(report: ReportArchiveItem) {
  return isEventEvidenceReviewReport(report) ? null : report.sourceCount;
}

export function GenericReportEvidenceAndMarkdown({ report }: { report: ReportArchiveItem }) {
  if (isEventEvidenceReviewReport(report)) return null;
  return <>
    <ResearchManifestEvidence
      reportId={report.id}
      summary={report.manifestSummary ?? { status: "metadata-incomplete", version: null, digest: null, reasons: ["Capture status is unavailable for this saved report."] }}
      canExport={report.canExportManifest === true}
    />
    <div className="analytics-result-body analytics-md reports-detail-body">
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{report.markdown ?? report.summary}</ReactMarkdown>
    </div>
  </>;
}

function formatDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

const ReportActions: React.FC<{ report: ReportArchiveItem }> = ({ report }) => {
  const [pdfStatus, setPdfStatus] = useState<"idle" | "working" | "error">("idle");
  const handleDownload = () => report.markdown && downloadMarkdown(
    report.markdown,
    `tsm-${report.symbol.toLowerCase()}-${report.researchType}-report.md`,
  );
  const handlePdfDownload = async () => {
    setPdfStatus("working");
    try {
      await downloadReportPdf({
        reportId: report.id,
        title: report.title,
        symbol: report.symbol,
        researchType: report.researchType,
        provider: report.provider,
        model: report.model,
        markdown: report.markdown,
      });
      setPdfStatus("idle");
    } catch {
      setPdfStatus("error");
      window.setTimeout(() => setPdfStatus("idle"), 3500);
    }
  };

  return (
    <div className="reports-detail-actions">
      <button type="button" className="analytics-action-btn" onClick={handleDownload} disabled={!report.markdown}>
        <Download size={14} aria-hidden="true" />
        <span>Download .md</span>
      </button>
      <button
        type="button"
        className="analytics-action-btn"
        onClick={handlePdfDownload}
        disabled={pdfStatus === "working"}
        title={pdfStatus === "error" ? "PDF export failed" : "Save report as PDF"}
      >
        {pdfStatus === "working"
          ? <Loader2 size={14} className="analytics-spin" aria-hidden="true" />
          : <FileDown size={14} aria-hidden="true" />}
        <span>{pdfStatus === "working" ? "PDF…" : pdfStatus === "error" ? "PDF failed" : "Save PDF"}</span>
      </button>
      <button type="button" className="analytics-action-btn" onClick={() => window.print()}>
        <Printer size={14} aria-hidden="true" />
        <span>Print</span>
      </button>
    </div>
  );
};

export function GenericReportActions({ report }: { report: ReportArchiveItem }) {
  return isEventEvidenceReviewReport(report) ? null : <ReportActions report={report} />;
}

export const ReportPdfCardButton: React.FC<{ report: ReportArchiveItem }> = ({ report }) => {
  const [pdfStatus, setPdfStatus] = useState<"idle" | "working" | "error">("idle");
  if (isEventEvidenceReviewReport(report)) return null;

  const handlePdfDownload = async () => {
    setPdfStatus("working");
    try {
      await downloadReportPdf({
        reportId: report.id,
        title: report.title,
        symbol: report.symbol,
        researchType: report.researchType,
        provider: report.provider,
        model: report.model,
      });
      setPdfStatus("idle");
    } catch {
      setPdfStatus("error");
      window.setTimeout(() => setPdfStatus("idle"), 3500);
    }
  };

  return (
    <button
      type="button"
      className="analytics-action-btn"
      onClick={handlePdfDownload}
      disabled={pdfStatus === "working"}
      title={pdfStatus === "error" ? "PDF export failed" : "Save report as PDF"}
    >
      {pdfStatus === "working"
        ? <Loader2 size={14} className="analytics-spin" aria-hidden="true" />
        : <FileDown size={14} aria-hidden="true" />}
      <span>{pdfStatus === "working" ? "PDF…" : pdfStatus === "error" ? "PDF failed" : "Save PDF"}</span>
    </button>
  );
};

const Reports: React.FC = () => {
  const [reports, setReports] = useState<ReportArchiveItem[]>([]);
  const [featured, setFeatured] = useState<ReportArchiveItem[]>([]);
  const [selected, setSelected] = useState<ReportArchiveItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [archiveFilter, setArchiveFilter] = useState<"all" | "analytics" | "intelligence-evidence">("all");

  const queryId = useMemo(() => {
    if (typeof window === "undefined") return null;
    return new URLSearchParams(window.location.search).get("id");
  }, []);

  const loadReports = async () => {
    setLoading(true);
    setError(null);
    try {
      const [archiveRes, featuredRes] = await Promise.all([
        fetch("/api/reports?limit=40", { headers: getClientIdentityHeaders(), credentials: "include" }),
        fetch("/api/reports/best-of", { headers: getClientIdentityHeaders(), credentials: "include" }),
      ]);
      const archiveData = await archiveRes.json();
      const featuredData = await featuredRes.json();
      if (!archiveRes.ok) throw new Error(archiveData.detail ?? archiveData.error ?? "Could not load archive");
      if (!featuredRes.ok) throw new Error(featuredData.detail ?? featuredData.error ?? "Could not load Best Of");
      setReports(archiveData.reports ?? []);
      setFeatured(featuredData.reports ?? []);
    } catch (err: any) {
      setError(err?.message ?? "Could not load reports");
    } finally {
      setLoading(false);
    }
  };

  const openReport = async (id: string) => {
    setDetailLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/reports/${encodeURIComponent(id)}`, {
        headers: getClientIdentityHeaders(),
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail ?? data.error ?? "Could not open report");
      setSelected(data.report);
      if (typeof window !== "undefined") {
        const url = new URL(window.location.href);
        url.searchParams.set("id", id);
        window.history.replaceState(null, "", url.toString());
      }
    } catch (err: any) {
      setError(err?.message ?? "Could not open report");
    } finally {
      setDetailLoading(false);
    }
  };

  const setFeaturedReport = async (report: ReportArchiveItem, nextFeatured: boolean) => {
    try {
      const res = await fetch(`/api/reports/${encodeURIComponent(report.id)}/featured`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          isFeatured: nextFeatured,
          excerpt: report.featuredExcerpt || report.summary,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail ?? data.error ?? "Could not update Best Of");
      await loadReports();
      if (selected?.id === report.id) setSelected(data.report);
    } catch (err: any) {
      setError(err?.message ?? "Could not update Best Of");
    }
  };

  useEffect(() => {
    loadReports();
  }, []);

  useEffect(() => {
    if (queryId) openReport(queryId);
  }, [queryId]);

  const renderCard = (report: ReportArchiveItem, featuredCard = false) => (
    <article key={report.id} className={featuredCard ? "reports-card reports-card--featured" : "reports-card"}>
      <div className="reports-card-topline">
        <span className="reports-type-pill">{reportDisplayLabel(report)}</span>
        {report.isFeatured && isPublicBestOfReport(report) && <span className="reports-featured-pill"><Star size={12} /> Best Of</span>}
      </div>
      <h2>{report.title}</h2>
      <p>{featuredCard ? report.featuredExcerpt || report.summary : report.summary}</p>
      <div className="reports-card-meta">
        <span>{report.symbol}</span>
        <span>{report.provider.toUpperCase()}</span>
        {reportCardSourceCount(report) !== null && <span>{reportCardSourceCount(report)} sources</span>}
        <span><Calendar size={12} /> {formatDate(report.createdAt)}</span>
      </div>
      <div className="reports-card-actions">
        <button type="button" className="analytics-action-btn" onClick={() => openReport(report.id)}>
          Open
        </button>
        <ReportPdfCardButton report={report} />
      </div>
    </article>
  );

  return (
    <>
      <Helmet>
        <title>Research Archive & Best Of | Tylee Smart Markets</title>
        <meta name="description" content="Saved Tylee Smart Markets research reports with a curated Best Of showcase." />
        <link rel="canonical" href="https://tyl.ee/reports" />
      </Helmet>
      <div className="tsm-home-shell theme-amber">
        <a href="#reports-main" className="skip-link">Skip to main content</a>
        <AppHeader brandText="Research Archive" />

        <main id="reports-main" className="reports-main">
          <section className="reports-toolbar">
            <div>
              <p className="home-kicker"><Archive size={15} aria-hidden="true" /> RESEARCH ARCHIVE</p>
              <h1>Saved research, curated highlights</h1>
            </div>
            <button type="button" className="analytics-action-btn" onClick={loadReports} disabled={loading}>
              {loading ? <Loader2 size={14} className="analytics-spin" /> : <Archive size={14} />}
              <span>Refresh</span>
            </button>
          </section>

          <ProspectiveCohortPanel />

          {error && (
            <section className="analytics-error" aria-live="polite" role="alert">
              <div className="analytics-error-header">
                <XCircle size={18} aria-hidden="true" />
                <span>{error}</span>
              </div>
            </section>
          )}

          {loading ? (
            <section className="analytics-progress" aria-live="polite">
              <div className="analytics-progress-header">
                <Loader2 size={18} className="analytics-spin" aria-hidden="true" />
                <span>Loading saved reports…</span>
              </div>
            </section>
          ) : (
            <>
              <section className="reports-section" aria-labelledby="best-of-title">
                <div className="reports-section-heading">
                  <h2 id="best-of-title">Best Of</h2>
                  <span>{featured.filter(isPublicBestOfReport).length} curated</span>
                </div>
                {featured.filter(isPublicBestOfReport).length > 0 ? (
                  <div className="reports-featured-grid">
                    {featured.filter(isPublicBestOfReport).map((report) => renderCard(report, true))}
                  </div>
                ) : (
                  <div className="reports-empty">
                    <CheckCircle size={18} aria-hidden="true" />
                    <span>Feature a saved report to build the public showcase.</span>
                  </div>
                )}
              </section>

              <section className="reports-section" aria-labelledby="archive-title">
                <div className="reports-section-heading">
                  <h2 id="archive-title">User Archive</h2>
                  <span>{reports.length} saved</span>
                </div>
                <label className="reports-archive-filter">Show reports
                  <select value={archiveFilter} onChange={(event) => setArchiveFilter(event.target.value as typeof archiveFilter)}>
                    <option value="all">All saved reports</option>
                    <option value="analytics">Analytics research</option>
                    <option value="intelligence-evidence">WHO evidence snapshots</option>
                  </select>
                </label>
                {(archiveFilter === "all" ? reports : reports.filter((report) => archiveFilter === "intelligence-evidence"
                  ? report.researchType === "intelligence-evidence"
                  : report.researchType !== "intelligence-evidence" && report.researchType !== "ideas")).length > 0 ? (
                  <div className="reports-grid">
                    {(archiveFilter === "all" ? reports : reports.filter((report) => archiveFilter === "intelligence-evidence"
                      ? report.researchType === "intelligence-evidence"
                      : report.researchType !== "intelligence-evidence" && report.researchType !== "ideas")).map((report) => renderCard(report))}
                  </div>
                ) : (
                  <div className="reports-empty">
                    <Archive size={18} aria-hidden="true" />
                    <span>{archiveFilter === "intelligence-evidence" ? "No saved WHO evidence snapshots." : archiveFilter === "analytics" ? "No saved Analytics reports." : "Run a research report from Analytics or save WHO evidence from Monitor."}</span>
                  </div>
                )}
              </section>
            </>
          )}

          {detailLoading && (
            <section className="analytics-progress" aria-live="polite">
              <div className="analytics-progress-header">
                <Loader2 size={18} className="analytics-spin" aria-hidden="true" />
                <span>Opening report…</span>
              </div>
            </section>
          )}

          {selected && (
            <section className="reports-detail" aria-labelledby="reports-detail-title">
              <div className="reports-detail-header">
                <div>
                  <p className="home-kicker">{reportDisplayLabel(selected)} · {selected.symbol}</p>
                  <h2 id="reports-detail-title">{isEventEvidenceReviewReport(selected) ? "Retained event evidence review" : selected.title}</h2>
                  {isIdeasRetentionReport(selected)
                    ? <p>This is the saved historical Ideas result. It uses the stored report copy and does not refresh or regenerate source or model content.</p>
                    : isEventEvidenceReviewReport(selected)
                    ? <p>Select a named version or receipt-verified UTC boundary in the review below.</p>
                    : <p>Report created: {formatInformationTime(selected.createdAt)} · {selected.provider.toUpperCase()}{selected.model ? ` · ${selected.model}` : ""}</p>}
                </div>
                <GenericReportActions report={selected} />
              </div>
              {!isEventEvidenceReviewReport(selected) && (isWhoResearchSnapshot(selected.snapshot)
                ? <SavedWhoResearchSnapshot snapshot={selected.snapshot} />
                : <SavedSnapshotInformation snapshot={selected.snapshot && "version" in selected.snapshot ? null : selected.snapshot} />)}
              {isExposureReactionReport(selected)
                ? <ExposureReactionDossierReport reportId={selected.id} onUpdated={openReport} />
                : isEventEvidenceReviewReport(selected)
                  ? <EventEvidenceReviewReport reportId={selected.id} onUpdated={openReport} />
                : selected.researchType !== "intelligence-evidence" && !isIdeasRetentionReport(selected) && <ResearchDossierReport reportId={selected.id} onUpdated={openReport} />}
              <GenericReportEvidenceAndMarkdown report={selected} />
            </section>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
};

export default Reports;
